import { morph } from '@theme/morph';

/**
 * Rebind refs and clear loading state on cart item components.
 * @param {Element} cartItemsEl
 */
function finalizeCartItemsComponent(cartItemsEl) {
  cartItemsEl.classList.remove('cart-items-disabled');

  if ('updatedCallback' in cartItemsEl && typeof cartItemsEl.updatedCallback === 'function') {
    queueMicrotask(() => cartItemsEl.updatedCallback());
  }
}

/**
 * Sync drawer empty-state class from freshly rendered section HTML.
 * @param {Document} fragment
 */
function syncCartDrawerDialogState(fragment) {
  const newDialog = fragment.querySelector('.cart-drawer__dialog');
  const liveDialog = document.querySelector('.cart-drawer__dialog');

  if (!newDialog || !liveDialog) return;

  liveDialog.classList.toggle('cart-drawer--empty', newDialog.classList.contains('cart-drawer--empty'));
}

/**
 * Find live cart-items targets for a section id.
 * @param {string} sectionId
 * @param {Document} fragment
 * @returns {{ newCartItems: Element | null, targets: Element[] }}
 */
function findCartItemsTargets(sectionId, fragment) {
  const newCartItems = fragment.querySelector('cart-items-component');
  if (!newCartItems) {
    return { newCartItems: null, targets: [] };
  }

  let targets = Array.from(document.querySelectorAll('cart-items-component')).filter(
    (el) => el.dataset.sectionId === sectionId
  );

  if (!targets.length) {
    const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
    const sectionTarget = sectionEl?.querySelector('cart-items-component');
    if (sectionTarget) targets = [sectionTarget];
  }

  if (!targets.length && fragment.querySelector('.cart-drawer__dialog')) {
    const drawerTarget = document.querySelector('cart-drawer-component cart-items-component');
    if (drawerTarget) targets = [drawerTarget];
  }

  return { newCartItems, targets };
}

/**
 * After cart HTML swaps, .cart-drawer__items often ends up with height 0.
 * Pin items + summary with absolute inset so scrolling does not depend on flex.
 * Never touch dialog display/animation (that replays slide-in).
 */
export function ensureCartDrawerScroll() {
  const dialog = document.querySelector('.cart-drawer__dialog');

  document.querySelectorAll('cart-items-component').forEach((el) => {
    el.classList.remove('cart-items-disabled');
  });

  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  if (dialog.hasAttribute('scroll-lock')) {
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.classList.add('cart-drawer-open');
  }

  const inner = dialog.querySelector('.cart-drawer__inner');
  const cartItems = dialog.querySelector('.cart-drawer__inner > cart-items-component');
  const content = dialog.querySelector('.cart-drawer__content');
  const header = dialog.querySelector('.cart-drawer__header');
  const items = dialog.querySelector('.cart-drawer__items');
  const summary = dialog.querySelector('.cart-drawer__summary');

  if (inner instanceof HTMLElement) {
    inner.style.height = '100%';
    inner.style.minHeight = '0';
    inner.style.overflow = 'hidden';
    inner.style.display = 'flex';
    inner.style.flexDirection = 'column';
  }

  if (cartItems instanceof HTMLElement) {
    cartItems.style.display = 'flex';
    cartItems.style.flexDirection = 'column';
    cartItems.style.flex = '1 1 0%';
    cartItems.style.height = '100%';
    cartItems.style.minHeight = '0';
    cartItems.style.overflow = 'hidden';
    cartItems.style.width = '100%';
  }

  if (header instanceof HTMLElement) {
    header.style.flex = '0 0 auto';
  }

  if (content instanceof HTMLElement) {
    content.style.position = 'relative';
    content.style.flex = '1 1 0%';
    content.style.minHeight = '0';
    content.style.height = 'auto';
    content.style.overflow = 'hidden';
    content.style.display = 'block';
  }

  void dialog.offsetHeight;

  const summaryH = summary instanceof HTMLElement ? Math.ceil(summary.getBoundingClientRect().height) : 0;

  if (summary instanceof HTMLElement) {
    summary.style.position = 'absolute';
    summary.style.left = '0';
    summary.style.right = '0';
    summary.style.bottom = '0';
    summary.style.zIndex = '2';
  }

  if (items instanceof HTMLElement) {
    items.style.position = 'absolute';
    items.style.left = '0';
    items.style.right = '0';
    items.style.top = '0';
    items.style.bottom = `${summaryH}px`;
    items.style.height = 'auto';
    items.style.maxHeight = 'none';
    items.style.minHeight = '0';
    items.style.overflowY = 'scroll';
    items.style.overflowX = 'hidden';
    items.style.webkitOverflowScrolling = 'touch';
    items.style.overscrollBehavior = 'contain';
    items.style.touchAction = 'pan-y';
    void items.offsetHeight;
  }
}

/** Remove duplicate checkout popups left after cart HTML swaps. */
function cleanupOrphanCheckoutPopups() {
  const popups = Array.from(document.querySelectorAll('#cart-checkout-popup'));
  if (popups.length <= 1) return;

  const keep =
    popups.find((el) => el instanceof HTMLDialogElement && el.open) || popups[popups.length - 1];

  popups.forEach((el) => {
    if (el !== keep) el.remove();
  });
}

/** @type {Record<string, string>} */
let pendingCartSections = {};

/** @type {number | null} */
let cartSectionsFlushId = null;

function flushCartSections() {
  cartSectionsFlushId = null;
  const batch = pendingCartSections;
  pendingCartSections = {};

  if (!Object.keys(batch).length) return;

  cleanupOrphanCheckoutPopups();

  Object.entries(batch).forEach(([sectionId, html]) => {
    applyCartSection(sectionId, html);
  });

  applyCartDrawerFallback(batch);
  ensureCartDrawerScroll();
  requestAnimationFrame(() => {
    ensureCartDrawerScroll();
    requestAnimationFrame(ensureCartDrawerScroll);
  });

  try {
    document.dispatchEvent(new CustomEvent('cart:sections-updated', { bubbles: true }));
  } catch (_) {}
}

/**
 * Replace cart drawer/items markup from a rendered section HTML string.
 * @param {string} sectionId
 * @param {string} html
 */
function applyCartSection(sectionId, html) {
  if (!html || !sectionId) return;

  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const { newCartItems, targets } = findCartItemsTargets(sectionId, fragment);

  if (!newCartItems || !targets.length) {
    console.warn('[cart-update] No cart-items target for section', sectionId);
    return;
  }

  targets.forEach((target) => {
    target.innerHTML = newCartItems.innerHTML;
    finalizeCartItemsComponent(target);
  });

  syncCartDrawerDialogState(fragment);
}

function applyCartDrawerFallback(sections) {
  const drawerItems = document.querySelector('cart-drawer-component cart-items-component');
  if (!drawerItems || drawerItems.querySelector('.cart-drawer__summary')) return;

  for (const [, html] of Object.entries(sections)) {
    if (!html || !html.includes('cart-drawer__summary')) continue;

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newCartItems = fragment.querySelector('cart-items-component');
    if (!newCartItems?.querySelector('.cart-drawer__summary')) continue;

    drawerItems.innerHTML = newCartItems.innerHTML;
    finalizeCartItemsComponent(drawerItems);
    syncCartDrawerDialogState(fragment);
    break;
  }
}

/** @deprecated Use applyCartSection via updateCartSection */
export function updateCartSection(sectionId, html) {
  applyCartSection(sectionId, html);
  ensureCartDrawerScroll();
}

/**
 * @param {Record<string, string>} sections
 */
export function updateCartSections(sections) {
  if (!sections || typeof sections !== 'object') return;

  Object.assign(pendingCartSections, sections);

  if (cartSectionsFlushId !== null) {
    cancelAnimationFrame(cartSectionsFlushId);
  }

  cartSectionsFlushId = requestAnimationFrame(flushCartSections);
}

/** @deprecated Use updateCartSection — kept for existing imports */
export function morphSectionCartItems(sectionId, html) {
  updateCartSection(sectionId, html);
}

if (typeof window !== 'undefined') {
  window.Shopify = window.Shopify || {};
  window.Shopify.updateCartSections = updateCartSections;
  window.Shopify.updateCartSection = updateCartSection;
  window.Shopify.ensureCartDrawerScroll = ensureCartDrawerScroll;
}

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {Map<string, AbortController>} */
  #abortControllersBySectionId = new Map();

  /** @type {Map<string, Promise<string>>} */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};

    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      updateCartSection(sectionId, sectionHTML);
      ensureCartDrawerScroll();
    }

    return sectionHTML;
  }

  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => response.text());

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}

function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();
